import type { TFunction } from 'i18next';

import type { PercentageBarItem } from '@/components/organisms/PercentageBars';
import type {
    HealthOverviewParameter,
    HealthScoreMetrics,
    HealthTrendPoint,
} from '@/domain/health';

import { toHealthPercent } from '../_utils/healthDetailOverviewHelpers';

import { HEALTH_DEVELOPMENT_CHART_COLORS } from './healthChartColors';
import type { HealthDevelopmentLineChartDataset } from './healthDevelopmentChartHelpers';

function resolveScoreSnapshot(parameter: HealthOverviewParameter): HealthScoreMetrics | null {
    return parameter.daily ?? parameter.weekly;
}

const EMPTY_DAY_SCORES: HealthScoreMetrics = {
    score0: null,
    score1: null,
    score2: null,
    score3: null,
    index: null,
    slope: null,
    count: null,
};

function trendPointHasScores(point: HealthTrendPoint): boolean {
    return (
        point.score0 != null ||
        point.score1 != null ||
        point.score2 != null ||
        point.score3 != null ||
        point.count != null
    );
}

/**
 * Score bars for the selected range. The trend is already clipped to that range, so its last
 * measured day is the chosen end date (or the last day before it that has a measurement).
 * The request's `daily` field stays the latest day and must not fill a historical range.
 */
export function withScoresOnSelectedDay(
    parameter: HealthOverviewParameter
): HealthOverviewParameter {
    const point = [...parameter.trend].reverse().find(trendPointHasScores);
    if (point == null) {
        return { ...parameter, daily: EMPTY_DAY_SCORES };
    }

    return {
        ...parameter,
        daily: {
            score0: point.score0,
            score1: point.score1,
            score2: point.score2,
            score3: point.score3,
            index: point.index,
            slope: null,
            count: point.count,
        },
    };
}

function mapTrendSeries(
    parameter: HealthOverviewParameter,
    scoreKey: 'score1' | 'score2' | 'score3'
): { day: string; value: number | null }[] {
    return parameter.trend.map((point) => ({
        day: point.day,
        value: toHealthPercent(point[scoreKey]),
    }));
}

export function mapParameterToLaksvelScoreBars(
    parameter: HealthOverviewParameter,
    t: TFunction
): PercentageBarItem[] {
    const scores = resolveScoreSnapshot(parameter);
    if (scores == null) {
        return [];
    }

    return [
        {
            label: t('dashboard.laksvelScore1'),
            value: toHealthPercent(scores.score1),
            color: HEALTH_DEVELOPMENT_CHART_COLORS.yellow,
        },
        {
            label: t('dashboard.laksvelScore2'),
            value: toHealthPercent(scores.score2),
            color: HEALTH_DEVELOPMENT_CHART_COLORS.orange,
        },
        {
            label: t('dashboard.laksvelScore3'),
            value: toHealthPercent(scores.score3),
            color: HEALTH_DEVELOPMENT_CHART_COLORS.red,
        },
    ];
}

export function mapParameterToTrendDatasets(
    parameter: HealthOverviewParameter,
    t: TFunction
): HealthDevelopmentLineChartDataset[] {
    return [
        {
            label: t('dashboard.laksvelScore1'),
            color: HEALTH_DEVELOPMENT_CHART_COLORS.yellow,
            data: mapTrendSeries(parameter, 'score1'),
        },
        {
            label: t('dashboard.laksvelScore2'),
            color: HEALTH_DEVELOPMENT_CHART_COLORS.orange,
            data: mapTrendSeries(parameter, 'score2'),
        },
        {
            label: t('dashboard.laksvelScore3'),
            color: HEALTH_DEVELOPMENT_CHART_COLORS.red,
            data: mapTrendSeries(parameter, 'score3'),
        },
    ];
}

/** Fish count for dashboard alert cards — latest day, else 7-day weekly rollup. */
export function getParameterFishCount(parameter: HealthOverviewParameter): number | null {
    return parameter.daily?.count ?? parameter.weekly?.count ?? null;
}

/** Fish counted on `context.asOf` only (detail information panel). */
export function getParameterFishCountOnLastMeasurementDay(
    parameter: HealthOverviewParameter
): number | null {
    return parameter.daily?.count ?? null;
}
